import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { FileInfo } from '../core/FileTransferService';
import { TransferStatus } from '../core/FileTransferService';

interface FileTransferPanelProps {
  transfer: {
    id: string;
    fileName: string;
    totalFiles: number;
    totalSize: number;
    transferredBytes: number;
    status: string;
    startTime: number;
    files: FileInfo[];
  };
  onClose: () => void;
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return (
        <svg className="w-3 h-3 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      );
    case 'failed':
      return (
        <svg className="w-3 h-3 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
        </svg>
      );
    case 'transferring':
      return (
        <svg className="w-3 h-3 text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      );
    default:
      return (
        <svg className="w-2 h-2 text-gray-400" fill="currentColor" viewBox="0 0 8 8">
          <circle cx="4" cy="4" r="3" />
        </svg>
      );
  }
};

const FileTransferPanel: React.FC<FileTransferPanelProps> = ({ transfer, onClose }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const transferringFileRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isUserScrollingRef = useRef(false);
  const isProgramScrollingRef = useRef(false);

  const progress = transfer.totalSize > 0
    ? (transfer.transferredBytes / transfer.totalSize * 100)
    : 0;

  const isCompleted = transfer.status === TransferStatus.COMPLETED;
  const isFailed = transfer.status === TransferStatus.FAILED;

  // Find the currently transferring file index
  const transferringFileIndex = transfer.files.findIndex(f => f.status === 'transferring');

  // Scroll to transferring file (center it in view)
  const scrollToTransferringFile = useCallback(() => {
    const container = scrollContainerRef.current;
    const element = transferringFileRef.current;
    if (!container || !element) return;

    // Get element position relative to container
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    // Current scroll position
    const currentScroll = container.scrollTop;

    // Element's top position relative to container's visible area
    const elementTopInContainer = elementRect.top - containerRect.top + currentScroll;

    // Calculate target scroll to center the element
    const containerHeight = container.clientHeight;
    const elementHeight = element.offsetHeight;
    const targetScrollTop = elementTopInContainer - (containerHeight / 2) + (elementHeight / 2);

    // Mark as program scrolling to avoid triggering user scroll detection
    isProgramScrollingRef.current = true;

    // Smooth scroll to target
    container.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: 'smooth',
    });

    // Reset after scroll animation (300ms is typical smooth scroll duration)
    setTimeout(() => {
      isProgramScrollingRef.current = false;
    }, 350);
  }, []);

  // Handle scroll events - user is manually scrolling
  const handleScroll = useCallback(() => {
    // Ignore program-triggered scrolls
    if (isProgramScrollingRef.current) return;

    isUserScrollingRef.current = true;

    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // After 3 seconds of no scrolling, auto-scroll back to transferring file
    scrollTimeoutRef.current = setTimeout(() => {
      isUserScrollingRef.current = false;
      scrollToTransferringFile();
    }, 3000);
  }, [scrollToTransferringFile]);

  // Auto-scroll to transferring file when file changes (unless user is scrolling)
  useEffect(() => {
    console.log('[FileTransferPanel] transferringFileIndex changed:', transferringFileIndex);
    if (!isUserScrollingRef.current && transferringFileIndex >= 0) {
      console.log('[FileTransferPanel] Scrolling to file', transferringFileIndex);
      scrollToTransferringFile();
    }
  }, [transferringFileIndex, scrollToTransferringFile]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Minimized view
  if (isMinimized) {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[150]">
        <div
          className="bg-gray-900/95 backdrop-blur-sm rounded-full px-4 py-2 shadow-2xl border border-white/10 flex items-center gap-3 cursor-pointer hover:bg-gray-800/95 transition-colors"
          onClick={() => setIsMinimized(false)}
        >
          <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <span className="text-sm text-white">{transfer.totalFiles} files</span>
          <div className="w-24 h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                isCompleted ? 'bg-green-500' : isFailed ? 'bg-red-500' : 'bg-blue-500'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-xs text-gray-400">{progress.toFixed(0)}%</span>
          {isCompleted && (
            <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
      </div>
    );
  }

  // Full view
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[150] w-[400px] max-w-[90vw]">
      <div className="bg-gray-900/95 backdrop-blur-sm rounded-xl shadow-2xl border border-white/10 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <span className="text-sm font-medium text-white">File Transfer</span>
          </div>
          <div className="flex items-center gap-1">
            {/* Minimize button */}
            <button
              onClick={() => setIsMinimized(true)}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              title="Minimize"
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </button>
            {/* Close button */}
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              title="Close"
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Overall progress */}
        <div className="px-4 py-3 border-b border-white/10">
          <div className="flex justify-between text-xs text-gray-400 mb-1.5">
            <span>{transfer.totalFiles} files</span>
            <span>{formatSize(transfer.transferredBytes)} / {formatSize(transfer.totalSize)}</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                isCompleted ? 'bg-green-500' : isFailed ? 'bg-red-500' : 'bg-blue-500'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between text-xs mt-1.5">
            <span className={`${
              isCompleted ? 'text-green-400' : isFailed ? 'text-red-400' : 'text-blue-400'
            }`}>
              {isCompleted ? 'Completed' : isFailed ? 'Failed' : transfer.status === 'pending' ? 'Preparing...' : 'Transferring...'}
            </span>
            <span className="text-gray-400">{progress.toFixed(1)}%</span>
          </div>
        </div>

        {/* File list */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="max-h-[200px] overflow-y-auto"
          style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.2) transparent' }}
        >
          {transfer.files.map((file) => {
            const fileProgress = file.size > 0 ? (file.transferredBytes / file.size * 100) : 0;
            const isTransferring = file.status === 'transferring';
            return (
              <div
                key={file.id}
                ref={isTransferring ? transferringFileRef : undefined}
                className={`flex items-center gap-2 px-3 py-1.5 border-b border-white/5 last:border-b-0 hover:bg-white/5 ${
                  isTransferring ? 'bg-blue-500/10' : ''
                }`}
              >
                {/* Status icon */}
                <div className="flex-shrink-0 w-3">
                  {getStatusIcon(file.status)}
                </div>

                {/* File info - single line */}
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  <span className="text-xs text-white truncate flex-1" title={file.path || file.name}>
                    {file.name}
                  </span>
                  <div className="w-16 h-1 bg-white/10 rounded-full overflow-hidden flex-shrink-0">
                    <div
                      className={`h-full transition-all duration-200 ${
                        file.status === 'completed' ? 'bg-green-500' :
                        file.status === 'failed' ? 'bg-red-500' :
                        file.status === 'transferring' ? 'bg-blue-500' : 'bg-gray-600'
                      }`}
                      style={{ width: `${fileProgress}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-gray-500 flex-shrink-0 w-14 text-right">
                    {formatSize(file.size)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FileTransferPanel;
